import type { State } from 'src/replicant/State';
import {
  babyAwakePeriod,
  FateAttributesStatusEffect,
  FatePermanentStatusEffect,
  FateState,
  FateStateRecord,
  sleepingPeriod,
} from './fate.ruleset';
import { PetState } from '../pet';
import * as Pet from '../pet';
import { getJSTDate } from 'src/replicant/utils/time';
import {
  getTimeUntilAwake as _getTimeUntilAwake,
  getTimeWhileAsleep,
} from './fate.status';
import { duration } from '../utils/duration';
import * as Visit from '../visit';
import { isKey } from '../utils/types';
import { puzzleCoinsIsPlayingTutorialDeterministic } from '../puzzlecoins/puzzlecoins.getters';

export type FateSchedule = { state: FateState; timestamp: number };

/**
 * Return permanent status effects summary from combined fate data and state data
 */
export function getPermanentStatusEffects(
  state: State,
  now: number,
): Record<
  | 'dead'
  | 'asleep'
  | 'dying'
  | 'evolving'
  | 'hadVisitor'
  | 'sick'
  | 'toothDecay'
  | 'scared'
  | 'wantToGoOut'
  | 'wantToInvite'
  | 'daycare',
  FateStateRecord
> {
  const result: Record<FatePermanentStatusEffect, FateStateRecord> = {
    evolving: {
      timestamp: getStatusEffectStartTime(state, 'evolving', now),
      value: hasPermanentStatusEffect(state, 'evolving', now),
    },
    sick: {
      timestamp: getStatusEffectStartTime(state, 'sick', now),
      value: hasPermanentStatusEffect(state, 'sick', now),
    },
    toothDecay: {
      timestamp: getStatusEffectStartTime(state, 'toothDecay', now),
      value: hasPermanentStatusEffect(state, 'toothDecay', now),
    },
    scared: {
      timestamp: getStatusEffectStartTime(state, 'scared', now),
      value: hasPermanentStatusEffect(state, 'scared', now),
    },
    asleep: {
      timestamp: getStatusEffectStartTime(state, 'asleep', now),
      value: hasPermanentStatusEffect(state, 'asleep', now),
    },
    dying: {
      timestamp: getStatusEffectStartTime(state, 'dying', now),
      value: hasPermanentStatusEffect(state, 'dying', now),
    },
    dead: {
      timestamp: getStatusEffectStartTime(state, 'dead', now),
      value: hasPermanentStatusEffect(state, 'dead', now),
    },
    hadVisitor: {
      timestamp: getStatusEffectStartTime(state, 'hadVisitor', now),
      value: hasPermanentStatusEffect(state, 'hadVisitor', now),
    },
    wantToGoOut: {
      timestamp: getStatusEffectStartTime(state, 'wantToGoOut', now),
      value: hasPermanentStatusEffect(state, 'wantToGoOut', now),
    },
    wantToInvite: {
      timestamp: getStatusEffectStartTime(state, 'wantToInvite', now),
      value: hasPermanentStatusEffect(state, 'wantToInvite', now),
    },
    daycare: {
      timestamp: getStatusEffectStartTime(state, 'daycare', now),
      value: hasPermanentStatusEffect(state, 'daycare', now),
    },
  };

  return result;
}

/**
 * Return status effects summary from combined fate and state data, similar to getStatusEffects but faster
 */
export function getAllPermanentStatusEffects(
  state: State,
  now: number,
): Record<FatePermanentStatusEffect, boolean> {
  const result: Record<FatePermanentStatusEffect, boolean> = {
    evolving: hasPermanentStatusEffect(state, 'evolving', now),
    sick: hasPermanentStatusEffect(state, 'sick', now),
    toothDecay: hasPermanentStatusEffect(state, 'toothDecay', now),
    scared: hasPermanentStatusEffect(state, 'scared', now),
    asleep: hasPermanentStatusEffect(state, 'asleep', now),
    dying: hasPermanentStatusEffect(state, 'dying', now),
    dead: hasPermanentStatusEffect(state, 'dead', now),
    hadVisitor: hasPermanentStatusEffect(state, 'hadVisitor', now),
    wantToGoOut: hasPermanentStatusEffect(state, 'wantToGoOut', now),
    wantToInvite: hasPermanentStatusEffect(state, 'wantToInvite', now),
    daycare: hasPermanentStatusEffect(state, 'daycare', now),
  };

  return result;
}

/**
 * Check if pet should be asleep.
 * @param state
 * @param now
 * @returns
 */
export function shouldBeAsleep(state: State, now: number): boolean {
  const { getCurrentEvolution, isInitialPet } = Pet.getters;
  const evolution = getCurrentEvolution(state);

  // No evolution, no sleep.
  if (!evolution) {
    return false;
  }

  // Pet should never be asleep during the tutorial - we add a 2 hour grace period after the last session.
  if (puzzleCoinsIsPlayingTutorialDeterministic(state)) {
    const timeSinceLastSession = now - (state.lastSessionAt ?? 0);
    if (timeSinceLastSession < duration({ hours: 2 })) {
      return false;
    }
  }

  // Initial pet should be awake for 2 hours, even if it's night time.
  const timeSinceCreated = now - evolution.timestamp;
  if (isInitialPet(state) && timeSinceCreated < babyAwakePeriod) {
    return false;
  }

  const jstHours = getJSTDate(now).getHours();
  const { startHour, endHour } = sleepingPeriod;
  return jstHours >= startHour || jstHours < endHour;
}

/**
 * Get safe delay. Considering sleep time. Will postpone message if pet is asleep.
 * @param state
 * @param now
 * @returns
 */
export function getSafeDelay(state: State, now: number, delay: number): number {
  return delay + getTimeUntilAwake(state, now + delay);
}

/**
 * Get time until pet is awake.
 * @param state
 * @param now
 * @returns
 */
export function getTimeUntilAwake(state: State, now: number): number {
  if (!shouldBeAsleep(state, now)) {
    return 0;
  }

  return _getTimeUntilAwake(now);
}

/**
 * Get the amount of time the pet is awake during the given period.
 */
export function getAwakeDuration(
  state: State,
  start: number,
  end: number,
): number {
  const { getCurrentEvolution, getPetCreatedAt, isInitialPet } = Pet.getters;
  const evolution = getCurrentEvolution(state);
  const petCreatedAt = getPetCreatedAt(state);

  if (!evolution?.stage) {
    return 0;
  }

  let awakeDuration = 0;

  // Ensure we're not accruing time before the pet was created
  start = Math.max(start, petCreatedAt);

  // Accrue any time that a newly created pet is awake during the night
  const babyAwakePeriodEnd = Math.min(petCreatedAt + babyAwakePeriod, end);
  if (isInitialPet(state) && start < babyAwakePeriodEnd) {
    awakeDuration += babyAwakePeriodEnd - start;
    start = babyAwakePeriodEnd;
  }

  // If we're starting while asleep, skip ahead to the morning
  start += getTimeUntilAwake(state, start);

  // Accrue time for each complete day between start and end
  const completeDays = Math.floor(
    Math.max(0, end - start) / duration({ days: 1 }),
  );
  awakeDuration +=
    completeDays *
    duration({
      hours: sleepingPeriod.startHour - sleepingPeriod.endHour,
    });
  start += duration({ days: completeDays });

  // If we're ending while asleep, skip back to the previous night
  let jstEnd = getJSTDate(end);
  if (jstEnd.getHours() < sleepingPeriod.endHour) {
    end -= duration({ hours: jstEnd.getHours() + 1 });
  }
  jstEnd = getJSTDate(end);
  if (jstEnd.getHours() >= sleepingPeriod.startHour) {
    end -= duration({
      hours: jstEnd.getHours() - sleepingPeriod.startHour,
      minutes: jstEnd.getMinutes(),
      seconds: jstEnd.getSeconds(),
      ms: jstEnd.getMilliseconds(),
    });
  }

  // Return current awake duration if we've skipped past the end time
  if (start >= end) {
    return awakeDuration;
  }

  // Accrue time for last partial day
  awakeDuration += end - start;

  // Subtract any overnight period
  if (getJSTDate(start).getDay() !== getJSTDate(end).getDay()) {
    awakeDuration -= duration({
      hours: 24 - sleepingPeriod.startHour + sleepingPeriod.endHour,
    });
  }

  return awakeDuration;
}

/**
 * Get sleeping duration
 */
export function getSleepingDuration(
  state: State,
  now: number,
  since: number,
): number {
  return now - since - getAwakeDuration(state, since, now);
}

/**
 * Check if pet already has one of status effects
 */
export function hasPermanentStatusEffect(
  state: State,
  status: FatePermanentStatusEffect,
  now: number,
): boolean {
  const schedule = state.fate.schedule;

  if (status === 'evolving') {
    // Ignore evolution for adult pets.
    const evolution = Pet.getters.getCurrentEvolution(state);
    if (evolution?.stage === 'adult') {
      return false;
    }
  }

  // Check sleeping first
  if (status === 'asleep' && shouldBeAsleep(state, now)) {
    return true;
  }

  // If status effect saved
  const savedEffect = state.pet.statusEffects[status];
  if (
    savedEffect.value &&
    now >= savedEffect.lastUpdatedAt &&
    now < (savedEffect.expiresAt ?? Number.POSITIVE_INFINITY)
  ) {
    return true;
  }

  // Check status effect schedule
  if (schedule[status] && now >= schedule[status].timestamp) {
    return true;
  }

  return false;
}

/**
 * Get permanents status effect start time
 */
export function getStatusEffectStartTime(
  state: State,
  status: FateState,
  now: number,
): number {
  const schedule = state.fate.schedule;

  // Check sleeping first
  if (status === 'asleep' && shouldBeAsleep(state, now)) {
    return now - getTimeWhileAsleep(now);
  }

  // Check status effect schedule
  if (schedule[status] && now >= schedule[status].timestamp) {
    return schedule[status].timestamp;
  }

  // If status effect saved
  const { statusEffects } = state.pet;
  if (isKey(status, statusEffects) && statusEffects[status]?.lastUpdatedAt) {
    return statusEffects[status].lastUpdatedAt;
  }

  return now;
}

/**
 * Get permanents status effect end time
 */
export function getPermanentStatusEffectEndTime(
  state: State,
  status: FatePermanentStatusEffect,
  now: number,
): number | undefined {
  if (status === 'asleep' && shouldBeAsleep(state, now)) {
    return now + _getTimeUntilAwake(now);
  }

  if (status === 'daycare' && hasPermanentStatusEffect(state, 'daycare', now)) {
    return state.pet.statusEffects.daycare?.expiresAt;
  }

  return now;
}

/**
 * Get pet attribute based state
 * Attribute status effects based on time and do not use rnd,
 * that are functions of time and cold dynamically changed
 */
export function getAttributeStatusEffect(
  attributes: Record<'dirtiness' | 'love' | 'stomach' | 'urine', number>,
  stress: number,
): FateAttributesStatusEffect {
  let result: FateAttributesStatusEffect = 'normal';

  switch (true) {
    case attributes.dirtiness > 70:
      result = 'very_dirty';
      break;
    case attributes.stomach < 15:
      result = 'very_hungry';
      break;
    case attributes.love < 15:
      result = 'very_lonely';
      break;
    case attributes.urine > 85:
      result = 'very_pee';
      break;
    case attributes.dirtiness > 40:
      result = 'dirty';
      break;
    case attributes.stomach < 40:
      result = 'hungry';
      break;
    case attributes.love < 40:
      result = 'lonely';
      break;
    case attributes.urine > 60:
      result = 'pee';
      break;
    case stress < 15:
      result = 'happy';
      break;
    default:
      result = 'normal';
  }

  return result;
}

/**
 * Get timestamp of scheduled pet state
 */
export const getScheduledPetState = (state: State, petState: FateState) =>
  state.fate.schedule[petState]?.timestamp;

/**
 * Get current pet state
 */
export function getCurrentPetState(
  state: State,
  now: number,
  isVisiting = Visit.getters.isVisiting(state),
  ignoreSleep = false,
): FateState {
  // Return permanent states first
  // This function will work without this part but potentially it could speedup thing to O(1)
  if (hasPermanentStatusEffect(state, 'dead', now)) return 'dead';
  if (!ignoreSleep && hasPermanentStatusEffect(state, 'asleep', now))
    return 'asleep';
  if (hasPermanentStatusEffect(state, 'dying', now)) return 'dying';
  if (hasPermanentStatusEffect(state, 'sick', now)) return 'sick';
  if (!isVisiting) {
    if (hasPermanentStatusEffect(state, 'evolving', now)) return 'evolving';
    if (hasPermanentStatusEffect(state, 'wantToGoOut', now))
      return 'wantToGoOut';
    if (hasPermanentStatusEffect(state, 'wantToInvite', now))
      return 'wantToInvite';
  }
  if (hasPermanentStatusEffect(state, 'toothDecay', now)) return 'toothDecay';
  if (hasPermanentStatusEffect(state, 'scared', now)) return 'scared';

  // Get attribute based states from fate
  const fate = state.fate.schedule;
  const ids = Object.keys(fate) as FateState[];

  const schedule: { state: FateState; timestamp: number }[] = ids
    .map((id) => ({ state: id, timestamp: fate[id].timestamp }))
    .filter((item) => {
      if (!isVisiting) return true;

      // Exclude this states if visiting
      if (
        item.state === 'evolving' ||
        item.state === 'hadVisitor' ||
        item.state === 'wantToGoOut' ||
        item.state === 'wantToInvite'
      )
        return false;

      return true;
    })
    .sort((a, b) => b.timestamp - a.timestamp); // Reverse order

  const status = schedule.find((item) => now >= item.timestamp)?.state;

  // Return normal by default
  return status ?? 'normal';
}

/**
 * Returns the currently visible pet state
 * Used for indexing data tables, animations, etc.
 */
export function getVisiblePetState(
  state: State,
  now: number,
  isVisiting = Visit.getters.isVisiting(state),
  ignoreSleep = false,
): PetState {
  const fateStatus = getCurrentPetState(state, now, isVisiting, ignoreSleep);
  return Pet.ruleset.getPetStateFromFate(fateStatus);
}

export type NextPetState = { state: PetState; timestamp: number };

/**
 * Get next pet state
 */
export function getNextPetState(
  state: State,
  now: number,
): NextPetState | null {
  // Get earlier effects
  const currentEffects = getPermanentStatusEffects(state, now);

  // Keep only active status effects
  for (const key in currentEffects) {
    const index = key as FatePermanentStatusEffect;
    if (!currentEffects[index].value) delete currentEffects[index];
  }

  // Calculate current state
  const effects = {
    ...currentEffects,
    ...state.fate.schedule,
  };
  const ids = Object.keys(effects) as FatePermanentStatusEffect[];
  const schedule: FateSchedule[] = ids
    .map((id) => ({ state: id, timestamp: effects[id].timestamp }))
    .sort((a, b) => b.timestamp - a.timestamp); // Reverse order

  // Current state index
  const index = schedule.findIndex((item) => now >= item.timestamp);

  // No current state
  if (index === -1) return null;

  // Get next state
  const nextState = schedule[index - 1];
  // return null by default
  if (!nextState) return null;

  return {
    state: Pet.ruleset.getPetStateFromFate(nextState.state),
    timestamp: nextState.timestamp,
  };
}

/**
 * Prevent certain status changes from occurring in specific time window
 * If fate system schedule the status during the safe time, randomly reschedule it within 2 hours after the safe time ends.
 * e.g., Pets don’t die between 10pm and 9am.
 * If fate system schedules death status at 8am for example, reschedule it randomly sometime between 9am and 11am
 */
export function isSafeTime(
  now: number,
  safePeriod: { startHour: number; endHour: number },
): boolean {
  const jstHours = getJSTDate(now).getHours();
  const { startHour, endHour } = safePeriod;
  return jstHours >= startHour || jstHours < endHour;
}

/**
 * Get score depend hot is bad state of pet
 */
export function getPetStateScore(state: State, now: number): number {
  const status = getCurrentPetState(state, now, true);

  switch (status) {
    case 'dying':
      return 11;
    case 'sick':
      return 10;
    case 'toothDecay':
      return 9;
    case 'very_dirty':
      return 8;
    case 'very_hungry':
      return 7;
    case 'very_lonely':
      return 6;
    case 'very_pee':
      return 5;
    case 'dirty':
      return 4;
    case 'hungry':
      return 3;
    case 'lonely':
      return 2;
    case 'pee':
      return 1;
    default:
      return 0;
  }
}
